import { useEffect, useState, type FormEvent } from 'react';
import { ThemeRoot } from '@nyce/admin-kit';
import { logos } from './assets';
import { Button, TextField } from '@nyce/admin-kit/ui';
import { ApiError } from '@nyce/admin-kit/api';
import { api, storeLogin } from './session';
import { startHubSso } from './sso';
import { autoSsoRequested, hubOptions } from './environment';
import type { LoginOptions } from './api';
import { ThemeToggle, useTheme } from './theme';

const MESSAGES: Record<string, string> = {
  invalid_credentials: 'Wrong username or password.',
  scope_required: 'Your account has no Operations Hub access (needs an ophub:* scope).',
  auth_unavailable: 'The sign-in service is unavailable. Try again in a moment.',
};

/**
 * Sign-in at the NYCE operator tenant (nyce-logic-auth via the hub backend): username + password, or — when the hub
 * has a login app (`Auth:LoginAppId`) — federated through AUTH (Entra ID, `docs/saml-entra.md`).
 */
export function Login({ error: initialError }: { error?: string } = {}) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(initialError ?? null);
  const [loading, setLoading] = useState(false);
  const [sso, setSso] = useState<LoginOptions['sso']>(null);
  const [redirecting, setRedirecting] = useState(false);
  const { resolved } = useTheme();

  useEffect(() => {
    void hubOptions().then((o) => {
      setSso(o?.sso ?? null);
      // opened by another hub's switch: sign in with Microsoft right away (once; the way back drops the marker)
      if (o?.sso && autoSsoRequested() && !initialError) {
        setRedirecting(true);
        startHubSso(o.sso).catch((err: unknown) => {
          setRedirecting(false);
          setError(err instanceof Error ? err.message : 'Sign-in failed');
        });
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const federated = async () => {
    if (!sso) return;
    setRedirecting(true);
    setError(null);
    try {
      await startHubSso(sso);
    } catch (err) {
      setRedirecting(false);
      setError(err instanceof Error ? err.message : 'Sign-in failed');
    }
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      storeLogin(await api.login({ username: username.trim(), password }));
    } catch (err) {
      const code = err instanceof ApiError ? err.code : undefined;
      setError((code && MESSAGES[code]) || (err instanceof Error ? err.message : 'Login failed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="nyk-login">
      <ThemeToggle className="nyk-login-theme" />
      <ThemeRoot theme={resolved} style={{ background: 'transparent' }}>
        <div className="nyk-ui-login">
          <header className="nyk-ui-header">
            <img
              src={resolved === 'dark' ? logos.logoWhite : logos.logoBlack}
              alt="nyceOperations"
              className="nyk-login-logo oph-logo"
            />
          </header>
          <form className="nyk-ui-login-form" onSubmit={submit}>
            <TextField
              label="Username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              autoComplete="username"
              required
            />
            <TextField
              label="Password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              required
            />
            {error && (
              <div className="nyk-ui-login-error" role="alert">
                {error}
              </div>
            )}
            <Button type="submit" variant="secondary" loading={loading}>
              Sign In
            </Button>
            {sso && (
              <>
                <div className="oph-login-or" aria-hidden="true">
                  or
                </div>
                <Button type="button" variant="ghost" loading={redirecting} onClick={() => void federated()}>
                  Sign in with Microsoft
                </Button>
              </>
            )}
          </form>
        </div>
      </ThemeRoot>
    </div>
  );
}
